import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User, AuthResponse, LoginDto, RegisterDto, UserRole } from '../types';
import { authApi } from '../services/api';
import { getErrorMessage } from '../utils/errorUtils';
import { setSentryUser, clearSentryUser } from '../config/sentry';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start as true since we check auth on mount
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

interface AuthContextType {
  state: AuthState;
  login: (credentials: LoginDto) => Promise<User>;
  register: (userData: RegisterDto) => Promise<User>;
  logout: () => Promise<void>;
  /**
   * Clear local auth state WITHOUT calling the server logout endpoint.
   * Use after account deletion (COMP-1): the refresh token is already dead
   * server-side and calling /auth/logout would 401.
   */
  clearSession: () => void;
  clearError: () => void;
  checkAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const login = async (credentials: LoginDto): Promise<User> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response: AuthResponse = await authApi.login(credentials);

      // Store tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);

      // Create user object - handle both string and array role formats
      const user: User = {
        id: response.userId,
        email: response.email,
        firstName: response.firstName,
        lastName: response.lastName,
        profilePictureUrl: response.profilePictureUrl,
        role: response.role as UserRole | UserRole[],
        isActive: true,
        createdAt: new Date().toISOString(),
      };

      // Enrich error reports with the user id only (PII-minimal: no email/name)
      setSentryUser(user.id);

      dispatch({ type: 'AUTH_SUCCESS', payload: user });
      return user;
    } catch (error) {
      const message = getErrorMessage(error, 'Login failed');
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  const register = async (userData: RegisterDto): Promise<User> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response: AuthResponse = await authApi.register(userData);

      // Store tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);

      // Create user object - handle both string and array role formats
      const user: User = {
        id: response.userId,
        email: response.email,
        firstName: response.firstName,
        lastName: response.lastName,
        profilePictureUrl: response.profilePictureUrl,
        role: response.role as UserRole | UserRole[],
        isActive: true,
        createdAt: new Date().toISOString(),
      };

      // Enrich error reports with the user id only (PII-minimal: no email/name)
      setSentryUser(user.id);

      dispatch({ type: 'AUTH_SUCCESS', payload: user });
      return user;
    } catch (error) {
      const message = getErrorMessage(error, 'Registration failed');
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (error) {
      // Even if logout fails on server, clear local storage
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      clearSentryUser();
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const clearSession = (): void => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    clearSentryUser();
    dispatch({ type: 'AUTH_LOGOUT' });
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const checkAuth = async (): Promise<void> => {
    const token = localStorage.getItem('accessToken');

    if (!token) {
      dispatch({ type: 'AUTH_LOGOUT' });
      return;
    }

    try {
      dispatch({ type: 'AUTH_START' });
      const userData = await authApi.getCurrentUser();

      // Create user object - handle both string and array role formats
      const user: User = {
        id: userData.userId,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        profilePictureUrl: userData.profilePictureUrl,
        role: userData.role as UserRole | UserRole[],
        isActive: true,
        createdAt: new Date().toISOString(),
      };

      // Enrich error reports with the user id only (PII-minimal: no email/name)
      setSentryUser(user.id);

      dispatch({ type: 'AUTH_SUCCESS', payload: user });
    } catch (error) {
      // Token is invalid or expired
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      clearSentryUser();
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    state,
    login,
    register,
    logout,
    clearSession,
    clearError,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};